
import type { Dispatch } from 'redux';

import {
    SCREEN_SHARE_REMOTE_PARTICIPANTS_UPDATED,
    SET_CAR_MODE,
    SET_PRIORITY_CONTENT_STACK,
    SET_TILE_VIEW
} from './actionTypes';
import { shouldDisplayTileView } from './functions';

/**
 * Creates a (redux) action which replaces the unified LIFO priority content stack
 * of active presentation items (screen share, shared video, whiteboard, etherpad).
 *
 * @param {Array<{ id: string, type: string, timestamp: number }>} stack - The new
 * ordered stack (last element is the most recent / top of the stack).
 * @returns {{
 *     type: SET_PRIORITY_CONTENT_STACK,
 *     stack: Array<Object>
 * }}
 */
export function setPriorityContentStack(stack: Array<Object>) {
    return {
        type: SET_PRIORITY_CONTENT_STACK,
        stack
    };
}

/**
 * Creates a (redux) action which signals that the list of known remote participants
 * with screen shares has changed.
 *
 * @param {string} participantIds - The remote participants which currently have active
 * screen share streams.
 * @returns {{
 *     type: SCREEN_SHARE_REMOTE_PARTICIPANTS_UPDATED,
 *     participantId: string
 * }}
 */
export function setRemoteParticipantsWithScreenShare(participantIds: Array<string>) {
    return {
        type: SCREEN_SHARE_REMOTE_PARTICIPANTS_UPDATED,
        participantIds
    };
}

/**
 * Creates a (redux) action which signals to set the UI layout to be tiled view
 * or not.
 *
 * @param {boolean} enabled - Whether or not tile view should be shown.
 * @returns {{
 *     type: SET_TILE_VIEW,
 *     enabled: ?boolean
 * }}
 */
export function setTileView(enabled: ?boolean = false) {

    return {
        type: SET_TILE_VIEW,
        enabled
    };
}

/**
 * Creates a (redux) action which signals either to exit tile view if currently
 * enabled or enter tile view if currently disabled.
 *
 * @param {boolean} enabled - Whether or not tile view should be shown.
 *
 * @returns {Function}
 */
export function toggleTileView(enabled: ?boolean) {
    return (dispatch: Dispatch<any>, getState: Function) => {
        const tileViewActive = shouldDisplayTileView(getState());

        dispatch(setTileView(enabled || !tileViewActive));
    };
}

/**
 * Creates a (redux) action which tells whether we are in carmode.
 *
 * @param {boolean} enabled - Whether we are in carmode.
 * @returns {{
*     type: SET_CAR_MODE,
*    enabled: boolean
* }}
*/
export function setIsCarmode(enabled) {
    return {
        type: SET_CAR_MODE,
        enabled
    };
}
